import { useEffect, useState } from 'react';
import { MessageStatus } from 'structure/bridge/constants/messageStatus';
import { TTransaction } from 'structure/bridge/store/transactionsAtom';
import {
  getLayerZeroMessage,
  LayerZeroMessage,
} from 'structure/bridge/utils/layerZeroScan';

const POLL_INTERVAL_MS = 5_000;
const MAX_RETRY_INTERVAL_MS = 60_000;

export function useLZMessage(transaction: TTransaction) {
  const [message, setMessage] = useState<LayerZeroMessage>({
    status: transaction.status,
    dstTxHash: transaction.dstTxHash,
  });

  useEffect(() => {
    const controller = new AbortController();
    let timeout: ReturnType<typeof setTimeout> | undefined;
    let consecutiveFailures = 0;

    const poll = async () => {
      try {
        const nextMessage = await getLayerZeroMessage(
          transaction.srcTxHash,
          controller.signal
        );

        if (controller.signal.aborted) {
          return;
        }

        consecutiveFailures = 0;
        if (nextMessage) {
          setMessage((previousMessage) =>
            previousMessage.status === nextMessage.status &&
            previousMessage.dstTxHash === nextMessage.dstTxHash
              ? previousMessage
              : nextMessage
          );
          if (
            nextMessage.status === MessageStatus.DELIVERED ||
            nextMessage.status === MessageStatus.FAILED
          ) {
            return;
          }
        }

        timeout = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }

        consecutiveFailures += 1;
        console.error('LayerZero Scan status check failed', error);
        timeout = setTimeout(
          poll,
          Math.min(
            POLL_INTERVAL_MS * 2 ** Math.min(consecutiveFailures, 4),
            MAX_RETRY_INTERVAL_MS
          )
        );
      }
    };

    void poll();

    return () => {
      controller.abort();
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [transaction.srcTxHash]);

  return {
    messageReceived: message.status === MessageStatus.DELIVERED,
    message,
  };
}
